import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCart } from "../../actions/cartActions";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import styles from "./styles.module.css";

function NavScrollExample() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { cart, loading } = useSelector((s) => s.cartReducer);
  const { token } = useSelector((s) => s.authReducer);

  const isAuthed = Boolean(token && token !== "undefined");
  const userId = useMemo(() => {
    try { return isAuthed ? JSON.parse(token)?.id ?? null : null; }
    catch { return null; }
  }, [isAuthed, token]);

  // Fetch cart exactly once per user session
  const lastFetchedUserId = useRef(null);
  useEffect(() => {
    if (!isAuthed || !userId) return;
    if (lastFetchedUserId.current === userId) return;
    lastFetchedUserId.current = userId;
    dispatch(getCart(userId));
  }, [dispatch, isAuthed, userId]);

  // ====== SEARCH STATE ======
  const [showPopupSearch, setShowPopupSearch] = useState(false);
  const [keyword, setKeyword] = useState("");

  // ====== ADVANCED SEARCH STATE ======
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [engineType, setEngineType] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleData, setVehicleData] = useState({});
  const [reloadYear, setReloadYear] = useState(false);
  const [disableYearDropdown, setDisableYearDropdown] = useState(false);
  const [reloadTriggered, setReloadTriggered] = useState(false);

  // dropdown hover control
  const [shopOpen, setShopOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [b2bOpen, setB2bOpen] = useState(false);

  // Vehicles data (only when deps change)
  useEffect(() => {
    const fetchVehicleData = async () => {
      try {
        const totalPages = 8;
        const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
        const responses = await Promise.all(
          pages.map((p) =>
            axios.get(`${process.env.REACT_APP_API_URL}api/vehiclesdataapi/?page=${p}`)
          )
        );

        const merged = {};
        for (const res of responses) {
          const chunk = res.data || {};
          for (const mk of Object.keys(chunk)) {
            merged[mk] = merged[mk] || {};
            for (const md of Object.keys(chunk[mk])) {
              merged[mk][md] = merged[mk][md] || {};
              for (const yr of Object.keys(chunk[mk][md])) {
                const y = parseInt(yr, 10);
                merged[mk][md][y] = merged[mk][md][y] || { engine_types: [], vehicle_types: [] };
                const src = chunk[mk][md][yr] || {};
                merged[mk][md][y].engine_types = Array.from(
                  new Set([...(merged[mk][md][y].engine_types || []), ...(src.engine_types || [])])
                );
                merged[mk][md][y].vehicle_types = Array.from(
                  new Set([...(merged[mk][md][y].vehicle_types || []), ...(src.vehicle_types || [])])
                );
              }
            }
          }
        }
        setVehicleData(merged);

        if (make && model) {
          const allYears = Object.keys(merged[make]?.[model] || {}).map((n) => parseInt(n, 10));
          if (allYears.length) {
            const minY = Math.min(...allYears);
            const maxY = Math.max(...allYears);
            if (maxY - minY + 1 !== allYears.length && !reloadTriggered) triggerYearReload();
            else setDisableYearDropdown(false);
          }
        }
      } catch (err) {
        console.error("Error fetching vehicle data:", err);
        setDisableYearDropdown(false);
      }
    };
    fetchVehicleData();
  }, [make, model, reloadYear]);

  const triggerYearReload = () => {
    if (reloadTriggered) return;
    setReloadTriggered(true);
    setReloadYear(true);
    setDisableYearDropdown(true);
    setYear("");
    setEngineType("");
    setVehicleType("");
    setTimeout(() => {
      setReloadYear(false);
      setDisableYearDropdown(false);
      setReloadTriggered(false);
    }, 14000);
  };

  // normal search
  const handleNormalSearch = (e) => {
    if (e) e.preventDefault();
    if (keyword.trim()) {
      navigate(`/product?keyword=${encodeURIComponent(keyword)}&page=1`);
      setShowPopupSearch(false);
    }
  };

  // advanced search
  const handleAdvancedSearch = () => {
    const params = new URLSearchParams();
    if (make) params.append("make", make);
    if (model) params.append("model", model);
    if (year) params.append("year", year);
    if (engineType) params.append("engineType", engineType);
    if (vehicleType) params.append("vehicleType", vehicleType);
    navigate(`/product?${params.toString()}&page=1`);
    setShowPopupSearch(false);
  };

  const handleClear = () => {
    setMake(""); setModel(""); setYear(""); setEngineType(""); setVehicleType("");
  };

  const yearsForSelection =
    make && model
      ? Object.keys(vehicleData[make]?.[model] || {})
          .map((y) => parseInt(y, 10))
          .sort((a, b) => a - b)
      : [];
  const enginesForSelection =
    make && model && year ? vehicleData[make][model][year]?.engine_types || [] : [];
  const vehiclesForSelection =
    make && model && year ? vehicleData[make][model][year]?.vehicle_types || [] : [];

  const canHover =
    typeof window !== "undefined" && window.matchMedia("(hover:hover)").matches;

  return (
    <>
      <Navbar expand="lg" className={styles.navbarCustom} sticky="top">
        <Container fluid className={styles.navContainer}>
          <Navbar.Brand as={Link} to="/" className={styles.brand}>
            <img
              className={styles.brandLogo}
              src="https://app.kamsiparts.com/wp-content/uploads/2021/12/Kamsi_Quality_Logo_plainbg.png"
              alt="Kamsi Parts"
            />
          </Navbar.Brand>

          <div className={styles.vSep} aria-hidden="true" />
          <Navbar.Toggle aria-controls="main-nav" />

          <Navbar.Collapse id="main-nav">
            <Nav className={styles.navLinks}>
              <Link to="/" className={styles.navLink}>Home</Link>

              <NavDropdown
                title="Shop"
                id="shop"
                className={styles.navDropdown}
                show={shopOpen}
                onMouseEnter={() => canHover && setShopOpen(true)}
                onMouseLeave={() => canHover && setShopOpen(false)}
                onToggle={(s) => !canHover && setShopOpen(s)}
              >
                <NavDropdown.Item as={Link} to="/product">Products</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/oximo-product">Wipers</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/aliproduct">AliExpress</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/aspl">ASPL</NavDropdown.Item>
                
              </NavDropdown>

              <NavDropdown
                title="Resources"
                id="resources"
                className={styles.navDropdown}
                show={resourcesOpen}
                onMouseEnter={() => canHover && setResourcesOpen(true)}
                onMouseLeave={() => canHover && setResourcesOpen(false)}
                onToggle={(s) => !canHover && setResourcesOpen(s)}
              >
                <NavDropdown.Item href="https://app.kamsiparts.com/blog/">Blog</NavDropdown.Item>
                <NavDropdown.Item href="https://app.kamsiparts.com/about-us/">About Us</NavDropdown.Item>
                <NavDropdown.Item href="https://app.kamsiparts.com/faqs/">FAQs</NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item href="tel:+2349098370120">Call: +2349098370120</NavDropdown.Item>
              </NavDropdown>

              <NavDropdown
                title="B2B Portal"
                id="b2b"
                className={styles.navDropdown}
                show={b2bOpen}
                onMouseEnter={() => canHover && setB2bOpen(true)}
                onMouseLeave={() => canHover && setB2bOpen(false)}
                onToggle={(s) => !canHover && setB2bOpen(s)}
              >
                <NavDropdown.Item href="https://app.kamsiparts.com/workshop/">Workshops/Garages</NavDropdown.Item>
                <NavDropdown.Item href="https://app.kamsiparts.com/distributors-retailers/">Distributors/Retailers</NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item href="https://app.kamsiparts.com/fleet/">Fleets/Corporate Clients</NavDropdown.Item>
                <NavDropdown.Item href="https://app.kamsiparts.com/3rd-party-suppliers/">3rd Party Suppliers</NavDropdown.Item>
              </NavDropdown>
            </Nav>

            <div className={`${styles.vSep} ${styles.vSepTall}`} aria-hidden="true" />

            <Form className={styles.searchInline} onSubmit={handleNormalSearch}>
              <Form.Control
                type="text"
                placeholder="Search by keyword"
                aria-label="Search by keyword"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className={styles.searchInput}
              />
              <Button type="submit" variant="primary" className={styles.searchBtn}>Search</Button>
              <Button
                type="button"
                variant="outline-secondary"
                className={styles.advBtn}
                onClick={() => setShowPopupSearch(true)}
              >
                Advanced
              </Button>
            </Form>

            <div className={`${styles.vSep} ${styles.vSepTall}`} aria-hidden="true" />

            <div className={styles.actionArea}>
              <Link to="/company-dashboard" className={styles.iconBtn} aria-label="Account">
                {isAuthed ? <FontAwesomeIcon icon={faUser} /> : <span className={styles.loginText}>Login</span>}
              </Link>

              <div className={styles.vSepMini} aria-hidden="true" />

              <div className={styles.cart}>
                <Link
                  to="/cartView"
                  state={{ cartId: cart?.id }}
                  aria-label="Cart"
                  className={styles.iconBtn}
                >
                  <FontAwesomeIcon icon={faShoppingCart} />
                </Link>
                {/* Badge visible when not loading and quantity > 0 */}
                {!loading && (cart?.quantity || 0) > 0 && (
                  <div className={styles.bubble}>
                    <span>{cart.quantity}</span>
                  </div>
                )}
              </div>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Advanced Search Modal */}
      <Modal show={showPopupSearch} onHide={() => setShowPopupSearch(false)} centered>
        <Modal.Header closeButton><Modal.Title>Advanced Search</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="makeDropdown" className="mb-3">
              <Form.Label>Make</Form.Label>
              <Form.Select
                value={make}
                onChange={(e) => { setMake(e.target.value); setModel(""); setYear(""); setEngineType(""); setVehicleType(""); }}
              >
                <option value="">Select Make</option>
                {Object.keys(vehicleData).sort().map((mk) => (
                  <option key={mk} value={mk}>{mk}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {make && (
              <Form.Group controlId="modelDropdown" className="mb-3">
                <Form.Label>Model</Form.Label>
                <Form.Select value={model} onChange={(e) => setModel(e.target.value)}>
                  <option value="">Select Model</option>
                  {Object.keys(vehicleData[make] || {}).sort().map((md) => (
                    <option key={md} value={md}>{md}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {make && model && (
              <Form.Group controlId="yearDropdown" className="mb-3">
                <div className={styles.labelRow}>
                  <Form.Label className="mb-0">Year</Form.Label>
                  <button type="button" className={styles.reloadBtn} onClick={triggerYearReload}>Reload</button>
                </div>
                <Form.Select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  disabled={disableYearDropdown}
                >
                  <option value="">Select Year</option>
                  { (make && model ? Object.keys(vehicleData[make]?.[model] || {}) : [])
                      .map((y) => parseInt(y, 10)).sort((a, b) => a - b)
                      .map((y) => <option key={y} value={y}>{y}</option>)
                  }
                </Form.Select>
              </Form.Group>
            )}

            {make && model && year && (
              <Form.Group controlId="engineTypeDropdown" className="mb-3">
                <Form.Label>Engine Type</Form.Label>
                <Form.Select value={engineType} onChange={(e) => setEngineType(e.target.value)}>
                  <option value="">Select Engine Type</option>
                  {(vehicleData[make][model][year]?.engine_types || []).map((e) => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {make && model && year && engineType && (
              <Form.Group controlId="vehicleTypeDropdown" className="mb-0">
                <Form.Label>Vehicle Type</Form.Label>
                <Form.Select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
                  <option value="">Select Vehicle Type</option>
                  {(vehicleData[make][model][year]?.vehicle_types || []).map((v, i) => (
                    <option key={`${v}-${i}`} value={v}>{v}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPopupSearch(false)}>Close</Button>
          <Button variant="primary" onClick={handleAdvancedSearch}>Search</Button>
          <Button variant="outline-danger" onClick={handleClear}>Clear</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default NavScrollExample;
